import { useEffect, useRef, useState } from 'react';

interface ContentRect {
  width: number;
  height: number;
  top: number;
  right: number;
  bottom: number;
  left: number;
}

interface BorderBoxSize {
  blockSize: number;
  inlineSize: number;
}

interface ResizeObserverResult {
  contentRect: ContentRect;
  borderBoxSize: BorderBoxSize;
}

export const useResizeObserver = (targetRef): ResizeObserverResult => {
  const [contentRect, setContentRect] = useState<ContentRect>({
    width: 0,
    height: 0,
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  });
  const [borderBoxSize, setBorderBoxSize] = useState<BorderBoxSize>({
    blockSize: 0,
    inlineSize: 0,
  });

  const resizeObserver = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    if ('ResizeObserver' in window) {
      observe(ResizeObserver);
    } else {
      import('resize-observer-polyfill').then(observe);
    }

    function observe(ResizeObserver) {
      resizeObserver.current = new ResizeObserver((entries) => {
        const { width, height, top, right, bottom, left } =
          entries[0].contentRect;
        setContentRect({ width, height, top, right, bottom, left });
        const { blockSize, inlineSize } = entries[0].borderBoxSize[0];
        setBorderBoxSize({ blockSize, inlineSize });
      });
      if (targetRef.current && resizeObserver.current) {
        resizeObserver.current.observe(targetRef.current);
      }
    }

    return disconnect;
  }, [targetRef]);

  function disconnect() {
    if (resizeObserver.current) {
      resizeObserver.current.disconnect();
    }
  }

  return { contentRect, borderBoxSize };
};
